import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/analytics";
import { sendWelcomeEmail } from "@/lib/welcomeEmail";

type BuildOption =
  | "SaaS / Web App"
  | "Content Business"
  | "Service Business"
  | "E-commerce"
  | "Not sure yet";

type BlockerOption =
  | "No tech skills"
  | "No idea validation"
  | "No time"
  | "No money"
  | "No team";

const BUILD_OPTIONS: BuildOption[] = [
  "SaaS / Web App",
  "Content Business",
  "Service Business",
  "E-commerce",
  "Not sure yet",
];

const BLOCKER_OPTIONS: BlockerOption[] = [
  "No tech skills",
  "No idea validation",
  "No time",
  "No money",
  "No team",
];

const BUILD_TOOLS: Record<BuildOption, string[]> = {
  "SaaS / Web App": ["Lovable", "Bolt.new", "Cursor", "Supabase", "Railway"],
  "Content Business": ["Beehiiv", "Buffer", "Canva", "Notion", "Perplexity"],
  "Service Business": ["Carrd", "Tally", "Calendly", "HubSpot", "Crisp"],
  "E-commerce": ["Carrd", "Stripe", "Lemon Squeezy", "Razorpay", "Canva"],
  "Not sure yet": ["Perplexity", "Notion", "Tally", "Carrd", "Canva"],
};

const BLOCKER_TOOLS: Record<BlockerOption, string[]> = {
  "No tech skills": ["Lovable", "Bolt.new", "Carrd", "Tally", "Canva"],
  "No idea validation": ["Perplexity", "Grok", "Tally", "Canny", "PostHog"],
  "No time": ["Buffer", "Canva", "Loops", "Calendly", "Notion"],
  "No money": ["Carrd", "Tally", "Canva", "Beehiiv", "Supabase"],
  "No team": ["Slack", "Linear", "Notion", "Attio", "Crisp"],
};

const TOOL_DESC: Record<string, string> = {
  Lovable: "Build full apps with AI prompts",
  "Bolt.new": "Spin up web apps instantly",
  Cursor: "AI code editor for builders",
  Supabase: "Backend, auth, and database in minutes",
  Railway: "Deploy backends without DevOps",
  Beehiiv: "Newsletter platform for creators",
  Buffer: "Schedule social posts in bulk",
  Canva: "Design graphics without a designer",
  Notion: "Docs, wiki, and project hub",
  Perplexity: "AI-powered research and answers",
  Carrd: "Simple one-page sites fast",
  Tally: "Free forms that look great",
  Calendly: "Let people book your time",
  HubSpot: "Free CRM for early sales",
  Crisp: "Live chat and shared inbox",
  Stripe: "Accept payments anywhere online",
  "Lemon Squeezy": "Sell digital products with tax handled",
  Razorpay: "Payments built for India",
  Grok: "Fast AI for trends and insights",
  Canny: "Collect feedback and feature votes",
  PostHog: "Product analytics for builders",
  Loops: "Email automation for SaaS",
  Slack: "Team chat that scales",
  Linear: "Issue tracking devs love",
  Attio: "Modern flexible CRM",
};

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function FounderToolkitQuiz({ open, onClose }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [build, setBuild] = useState<BuildOption | null>(null);
  const [blocker, setBlocker] = useState<BlockerOption | null>(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    track("toolkit_quiz_started");
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const tools = useMemo(() => {
    if (!build || !blocker) return [];
    const a = BUILD_TOOLS[build];
    const b = new Set(BLOCKER_TOOLS[blocker]);
    const overlap = a.filter((t) => b.has(t));
    if (overlap.length >= 3) return overlap.slice(0, 5);
    const fill = a.filter((t) => !overlap.includes(t));
    return [...overlap, ...fill].slice(0, 5);
  }, [build, blocker]);

  const handleBuild = (o: BuildOption) => {
    setBuild(o);
    setStep(2);
  };
  const handleBlocker = (o: BlockerOption) => {
    setBlocker(o);
    setStep(3);
    track("toolkit_quiz_completed", { q1: build, q2: o });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke(
        "subscribe-toolkit",
        { body: { email, build, blocker } },
      );
      if (fnErr || (data as any)?.error) {
        throw new Error(fnErr?.message || (data as any)?.error || "Failed");
      }
      track("toolkit_subscribed", { utm_source: "toolkit_popup" });
      sendWelcomeEmail(email, "toolkit_popup_quiz");
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm animate-in fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[520px] bg-card brutal-border brutal-shadow rounded-md p-6 sm:p-8 animate-in zoom-in-95 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 rounded-md hover:bg-muted transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {step === 1 && (
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground">
              Find your free founder toolkit
            </h2>
            <p className="mt-2 text-muted-foreground">
              2 quick questions. We'll show you the exact AI tools for your situation.
            </p>
            <p className="mt-6 font-bold text-foreground">What are you trying to build?</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {BUILD_OPTIONS.map((o) => (
                <button
                  key={o}
                  onClick={() => handleBuild(o)}
                  className="px-4 py-2 rounded-full brutal-border bg-background hover:bg-primary hover:text-primary-foreground font-bold text-sm transition-colors"
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-xs font-bold text-muted-foreground tracking-widest uppercase">
              Step 2 of 2
            </p>
            <h2 className="mt-1 text-2xl sm:text-3xl font-black tracking-tight text-foreground">
              What's your biggest blocker right now?
            </h2>
            <div className="mt-6 flex flex-wrap gap-2">
              {BLOCKER_OPTIONS.map((o) => (
                <button
                  key={o}
                  onClick={() => handleBlocker(o)}
                  className="px-4 py-2 rounded-full brutal-border bg-background hover:bg-primary hover:text-primary-foreground font-bold text-sm transition-colors"
                >
                  {o}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && !success && (
          <div>
            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-foreground">
              Your founder toolkit
            </h2>
            <p className="mt-2 text-muted-foreground">
              Hand-picked for {build?.toLowerCase()} · {blocker?.toLowerCase()}
            </p>
            <ul className="mt-5 space-y-2">
              {tools.map((t) => (
                <li
                  key={t}
                  className="brutal-border bg-background rounded-md px-3 py-2"
                >
                  <span className="font-black text-foreground">{t}</span>
                  <span className="text-muted-foreground"> — {TOOL_DESC[t] ?? "Founder favorite"}</span>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-muted-foreground">
              Want the full breakdown with free credits for each tool? We'll send it to your inbox.
            </p>
            <form onSubmit={handleSubmit} className="mt-3 flex flex-col sm:flex-row gap-2">
              <Input
                type="email"
                required
                placeholder="you@startup.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="brutal-border h-11"
              />
              <Button type="submit" disabled={submitting} className="h-11 font-black">
                {submitting ? "Sending…" : "Send My Toolkit →"}
              </Button>
            </form>
            {error && <p className="mt-2 text-sm text-destructive font-bold">{error}</p>}
          </div>
        )}

        {step === 3 && success && (
          <div className="text-center py-6">
            <p className="text-2xl sm:text-3xl font-black tracking-tight text-foreground">
              🎉 Check your inbox!
            </p>
            <p className="mt-3 text-muted-foreground">
              Your founder toolkit is on its way.
            </p>
            <Button onClick={onClose} className="mt-6 font-black">
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
